<template>
<q-page class="main no-sticky-header">
  <div class="row justify-center">
    <q-card class="page-card col-xs-12 col-md-8">
      <q-tabs align="left" v-model="tab">
        <q-tab no-caps name="general">Informatie</q-tab>
        <q-tab v-if="sso.available"
               no-caps
               name="sso"
               :disable="!sso.configured && mfa.configured">
          Single Sign On
        </q-tab>
        <q-tab no-caps
               name="mfa"
               :disable="sso.configured">
          Multifactor authenticatie
        </q-tab>
      </q-tabs>
      <q-tab-panels v-model="tab">
        <q-tab-panel name="general">
          <common-form :action="$route('auth.update')" is-update>
            <q-card-section class="page-card__main-content">
              <h2 class="heading-2 q-mb-lg">Instellingen</h2>
              <div class="row q-col-gutter-lg">
                <div class="col-xs-12 col-sm-6">
                  <common-input
                      v-model="form.first_name"
                      label="Voornaam"
                      name="first_name"
                      autocomplete="given-name"
                      :disable="sso.configured"
                      :error-message="$errorMessage('first_name')"
                      :error="$hasError('first_name')"
                      required
                      autofocus
                  />
                </div>
                <div class="col-xs-12 col-sm-6">
                  <common-input
                      v-model="form.last_name"
                      label="Achternaam"
                      name="last_name"
                      autocomplete="family-name"
                      :disable="sso.configured"
                      :error-message="$errorMessage('last_name')"
                      :error="$hasError('last_name')"
                      required
                  />
                </div>
                <div class="col-xs-12 col-sm-6 q-mb-lg">
                  <common-input
                      v-model="form.phone_number"
                      label="Telefoonnummer"
                      type="tel"
                      name="phone_number"
                      autocomplete="tel"
                      :disable="sso.configured"
                      :error-message="$errorMessage('phone_number')"
                      :error="$hasError('phone_number')"
                  />
                </div>
              </div>
              <div v-if="!sso.configured"
                   class="row q-col-gutter-lg">
                <div class="col-xs-12 col-sm-6">
                  <common-input
                      v-model="form.password"
                      label="Wachtwoord"
                      :type="isPwd ? 'password' : 'text'"
                      name="password"
                      :error-message="$errorMessage('password')"
                      :error="$hasError('password')"
                  >
                    <template v-slot:append>
                      <q-btn flat
                             dense
                             round
                             :icon="isPwd ? 'visibility_off' : 'visibility'"
                             :aria-label="isPwd ? 'Toon wachtwoord' : 'Verberg wachtwoord' "
                             @click="isPwd = !isPwd"
                      />
                    </template>
                  </common-input>
                </div>
                <div class="col-xs-12 col-sm-6">
                  <common-input
                      v-model="form.new_email"
                      label="Nieuw e-mailadres"
                      type="email"
                      name="new_email"
                      auto-complete="email"
                      :error-message="$errorMessage('new_email')"
                      :error="$hasError('new_email')"
                  />
                </div>
                <div class="col-xs-12 col-sm-6">
                  <common-input
                      v-model="form.new_password"
                      label="Nieuw wachtwoord"
                      :type="isNewPwd ? 'password' : 'text'"
                      name="new_password"
                      autocomplete="new-password"
                      :error-message="$errorMessage('new_password')"
                      :error="$hasError('new_password')"
                  >
                    <template v-slot:append>
                      <q-btn flat
                             dense
                             round
                             :icon="isNewPwd ? 'visibility_off' : 'visibility'"
                             :aria-label="isNewPwd ? 'Toon wachtwoord' : 'Verberg wachtwoord' "
                             @click="isNewPwd = !isNewPwd"
                      />
                    </template>
                  </common-input>
                </div>
                <div class="col-xs-12 col-sm-6">
                  <common-input
                      v-model="form.new_password_repeat"
                      label="Herhaal nieuw wachtwoord"
                      :type="isNewPwdRepeat ? 'password' : 'text'"
                      name="new_password_repeat"
                      :error-message="$errorMessage('new_password_repeat')"
                      :error="$hasError('new_password_repeat')"
                  >
                    <template v-slot:append>
                      <q-btn flat
                             dense
                             round
                             :icon="isNewPwdRepeat ? 'visibility_off' : 'visibility'"
                             :aria-label="isNewPwdRepeat ? 'Toon wachtwoord' : 'Verberg wachtwoord' "
                             @click="isNewPwdRepeat = !isNewPwdRepeat"
                      />
                    </template>
                  </common-input>
                </div>
              </div>
              <div class="col-12" v-if="sso.configured">
                De gegevens van je account worden beheerd in Azure en kunnen hier niet bewerkt worden.
              </div>
            </q-card-section>
            <q-separator />
            <q-card-actions
                class="page-card__actions q-gutter-y-md"
                align="between"
            >
              <warning-button label="Verwijder mijn account" @click="deleteAccount" />
              <div>
                <accent-button v-if="!sso.configured" label="Opslaan" type="submit" class="q-mr-md" />
                <a :href="$route('dashboard')" class="link link--primary">Annuleren</a>
              </div>
            </q-card-actions>
          </common-form>
        </q-tab-panel>
        <q-tab-panel v-if="sso.available" name="sso">
          <q-card-section class="page-card__main-content">
            <h2 class="heading-2 q-mb-lg">Single Sign-On</h2>
            <p>Door Single Sign-On(SSO) te activeren kunt u voortaan eenvoudig inloggen met uw bestaande Microsoft Azure
              account. Hierdoor hoeft u niet telkens opnieuw in te loggen.</p>
            <template v-if="sso.configured">
              <p> Klik op onderstaande knop om uw HIOR account los te koppelen van uw Azure account.</p>
              <accentButton
                  @click="disconnectSSO"
                  class="q-mt-sm">Ontkoppel van Azure
              </accentButton>
            </template>
            <template v-else>
              <p> Klik op onderstaande knop om uw HIOR account aan uw Azure account te koppelen.</p>
              <accentButton
                  @click="$goto('auth.connect-azure')"
                  class="q-mt-sm">Koppel aan Azure
              </accentButton>
            </template>
          </q-card-section>
        </q-tab-panel>
        <q-tab-panel name="mfa">
          <q-card-section class="page-card__main-content">
            <h2 class="heading-2 q-mb-lg">Multifactor authenticatie</h2>
            <template v-if="mfa.recoveryCodes">
              <p>Hieronder worden eenmalig uw herstelcodes weergegeven. Bewaar deze codes goed op een veilige plek,
                bijvoorbeeld
                in een wachtwoordmanager, omdat ze u kunnen helpen toegang te krijgen tot uw account als u geen toegang
                heeft tot uw smartphone.</p>
              <ul>
                <li v-for="(code, index) in mfa.recoveryCodes"
                    :key="index">
                  <pre>{{ code }}</pre>
                </li>
              </ul>
              <accentButton
                  @click="$goto('auth.edit')"
                  class="q-mt-sm">Ik heb de codes opgeslagen
              </accentButton>
            </template>
            <template v-else>
              <template v-if="!mfa.svg">
                <p>Multifactor authenticatie (MFA) biedt een extra beveiligingslaag door het vereisen van een tweede
                  verificatiemethode naast uw gebruikersnaam en wachtwoord. Hiermee wordt uw account beter beschermd
                  tegen
                  ongeautoriseerde toegang.</p>
              </template>
              <template v-if="mfa.configured">
                <common-form :action="$route('two-factor.disable')" is-delete>
                  <accentButton
                      type="submit"
                      class="q-mt-sm">Deactiveer multifactor authenticatie
                  </accentButton>
                </common-form>
              </template>
              <template v-else-if="mfa.svg">
                <common-form :action="$route('two-factor.confirm')">
                  <p>Scan de onderstaande QR code met uw autheticator app.</p>
                  <div class="mfaSVGContainer" v-html="mfa.svg" />
                  <p>
                    De laatste stap van het vereist dat u één code uit de app invoert om de koppeling te voltooien.
                  </p>
                  <div class="row q-mt-md">
                    <div class="col-md-6">
                      <common-input
                          v-model="mfaCode"
                          label="Code"
                          name="code"
                          required
                          autofocus
                          :error-message="mfa.error ? 'Onjuiste code' :''"
                          :error="mfa.error"
                      />
                    </div>
                    <div class="col-md-6">
                      <accentButton
                          type="submit"
                          class="q-ml-md">Koppel
                      </accentButton>
                    </div>
                  </div>
                </common-form>
              </template>
              <template v-else>
                <p>Om de koppeling te maken heeft u een applicatie op uw smartphone nodig die verificatie codes kan
                  genereren. Deze mobiele apps, zoals Google
                  Authenticator(<a
                      href="https://play.google.com/store/apps/details?id=com.google.android.apps.authenticator2"
                      title=">Google authenticator voor Android">Android</a> of
                  <a href="https://apps.apple.com/us/app/google-authenticator/id388497605"
                     title=">Google authenticator voor iOS">iOS</a> ),
                  <a href="https://www.microsoft.com/en-us/account/authenticator">Microsoft
                    Authenticator</a> en <a href="https://authy.com/">Authy</a> of enig
                  andere applicatie die TOTP codes kan genereren, bieden een handige en veilige manier om codes te
                  genereren op uw smartphone. Deze apps kunt u vinden via de App Store of Google Play.</p>
                <common-form :action="$route('two-factor.enable')">
                  <accentButton
                      type="submit"
                      class="q-mt-sm">Activeer multifactor authenticatie
                  </accentButton>
                </common-form>
              </template>
            </template>
          </q-card-section>
        </q-tab-panel>
      </q-tab-panels>
    </q-card>
  </div>
</q-page>
</template>

<script>
export default {
  props: {
    user: {
      type: Object,
      required: true,
    },
    sso: {
      type: Object,
      required: true,
    },
    mfa: {
      type: Object,
      required: true,
    },
    startTab: {
      type: String,
      default: 'general',
    },
  },
  data () {
    return {
      tab: this.startTab,
      mfaCode: '',
      isPwd: true,
      isNewPwd: true,
      isNewPwdRepeat: true,
      form: {
        first_name: this.$old('first_name', this.user?.first_name),
        last_name: this.$old('last_name', this.user?.last_name),
        phone_number: this.$old('phone_number', this.user?.phone_number),
        new_email: this.$old('new_email'),
        new_password: '',
        new_password_repeat: '',
      },
    }
  },
  methods: {
    async disconnectSSO () {
      if (!await this.$confirm(
          {
            title: 'SSO Ontkoppelen',
            messages: [
              'Weet u zeker dat u de SSO koppeling van uw account wilt opheffen? U wordt uitgelogd en kan enkel opnieuw inloggen met uw email en wachtwoord',
              'Als u uw wachtwoord niet meer weet, kunt u een wachtwoord reset aanvragen.',
            ],
          },
      )) {
        return
      }
      await axios.delete(this.$route('auth.disconnect-azure'))
      this.$toast('success', 'De koppeling is opgeheven')
      this.$goto('auth.login')
    },
    async deleteAccount () {
      if (!await this.$confirm(
          {
            title: 'Account verwijderen',
            messages: ['Weet u zeker dat u uw account wil verwijderen? U verliest hiermee uw toegang tot het HIOR platform. Dit kan niet worden teruggedraaid.'],
          },
      )) {
        return
      }
      await axios.delete(this.$route('auth.destroy'))
      this.$toast('success', 'Uw account is verwijderd')
      this.$goto('auth.login')
    },
  },
}
</script>
<style lang="scss">
.mfaSVGContainer {
  svg {
    width: 350px;
    height: 350px;
  }
}
</style>
